import { useEffect, useState } from "react";
import { api, ApiError, type Bucket } from "../lib/api";
import { formatCents } from "../lib/money";

/* The two things a child can do with money, kept as full-screen-ish panels
 * rather than dialogs: a modal on a shared tablet is easy to dismiss by
 * accident, and these both move real money. */

/* Presentation only. The AUTHORITATIVE list lives on the server and is
 * fetched below -- hardcoding it here as well would eventually let the UI
 * offer a category the server rejects. An unknown key still renders, just
 * without a picture. */
const CATEGORY_LABELS: Record<string, string> = {
  snacks: "🍦 Snacks",
  toys: "🧸 Toys",
  games: "🎮 Games",
  books: "📚 Books",
  gifts: "🎁 Gifts",
  clothes: "👕 Clothes",
  other: "✨ Something else",
};

function labelFor(key: string): string {
  return CATEGORY_LABELS[key] ?? key.charAt(0).toUpperCase() + key.slice(1);
}

export function AskForCash({
  childId,
  buckets,
  initialBucketId,
  onDone,
  onCancel,
}: {
  childId: string;
  buckets: Bucket[];
  /** Preselects the jar when opened from that jar's own detail panel. */
  initialBucketId?: string;
  onDone: () => void;
  onCancel: () => void;
}) {
  const withdrawable = buckets.filter((b) => b.withdrawable && b.balanceCents > 0);
  const [bucketId, setBucketId] = useState(
    () =>
      withdrawable.find((b) => b.id === initialBucketId)?.id ??
      withdrawable[0]?.id ??
      "",
  );
  const [amount, setAmount] = useState("");
  const [note, setNote] = useState("");
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);

  const chosen = buckets.find((b) => b.id === bucketId);

  if (withdrawable.length === 0) {
    return (
      <Panel title="Ask for cash" onCancel={onCancel}>
        <p style={{ margin: 0, color: "var(--ink-2)" }}>
          There&rsquo;s nothing in your jars to take out yet.
        </p>
      </Panel>
    );
  }

  return (
    <Panel title="Ask for cash" onCancel={onCancel}>
      <p style={{ margin: "0 0 14px", color: "var(--ink-2)", fontSize: 14 }}>
        A grown-up will give you real money and take it out of your jar.
      </p>

      <div style={{ display: "flex", gap: 8, flexWrap: "wrap", marginBottom: 14 }}>
        {withdrawable.map((b) => (
          <button
            key={b.id}
            type="button"
            aria-pressed={bucketId === b.id}
            onClick={() => setBucketId(b.id)}
            style={{
              minHeight: 52, fontSize: 15, boxShadow: "none",
              background: bucketId === b.id ? "var(--save-soft)" : "var(--paper)",
              borderColor: bucketId === b.id ? "var(--save)" : "var(--paper-edge)",
            }}
          >
            {b.name}{" "}
            <span className="money" style={{ color: "var(--ink-3)" }}>
              {formatCents(b.balanceCents)}
            </span>
          </button>
        ))}
      </div>

      <label style={{ display: "grid", gap: 6, marginBottom: 12 }}>
        <span style={{ fontWeight: 600, fontSize: 14 }}>How much?</span>
        <input
          inputMode="decimal"
          value={amount}
          onChange={(e) => setAmount(e.target.value)}
          className="money"
          placeholder="2.00"
          style={{ fontSize: 26, textAlign: "center", minHeight: 60 }}
        />
      </label>

      <label style={{ display: "grid", gap: 6, marginBottom: 16 }}>
        <span style={{ fontWeight: 600, fontSize: 14 }}>What&rsquo;s it for?</span>
        <input value={note} maxLength={140} onChange={(e) => setNote(e.target.value)}
               placeholder="the ice cream truck" />
      </label>

      {error && (
        <p role="alert" style={{ color: "var(--bad)", fontWeight: 600, marginTop: 0 }}>
          {error}
        </p>
      )}

      <button
        disabled={busy || !amount}
        style={{ width: "100%", minHeight: 56 }}
        onClick={async () => {
          const cents = Math.round(parseFloat(amount) * 100);
          if (!Number.isFinite(cents) || cents <= 0) {
            setError("Type how much you want.");
            return;
          }
          if (chosen && cents > chosen.balanceCents) {
            setError(`There's only ${formatCents(chosen.balanceCents)} in that jar.`);
            return;
          }
          setBusy(true);
          setError(null);
          try {
            await api.post(`/children/${childId}/cash-out`, {
              bucketId, amountCents: cents, note: note.trim(),
            });
            onDone();
          } catch (e) {
            setError(e instanceof ApiError ? e.message : "That didn't work.");
          } finally {
            setBusy(false);
          }
        }}
      >
        {busy ? "Asking…" : "Ask a grown-up"}
      </button>
    </Panel>
  );
}

export function LogSpending({
  childId,
  buckets,
  initialBucketId,
  onDone,
  onCancel,
}: {
  childId: string;
  buckets: Bucket[];
  /** Preselects the jar when opened from that jar's own detail panel. */
  initialBucketId?: string;
  onDone: () => void;
  onCancel: () => void;
}) {
  const withCash = buckets.filter((b) => b.walletCents > 0);
  const [bucketId, setBucketId] = useState(
    () =>
      withCash.find((b) => b.id === initialBucketId)?.id ??
      withCash[0]?.id ??
      "",
  );
  const [amount, setAmount] = useState("");
  const [categories, setCategories] = useState<string[]>([]);
  const [category, setCategory] = useState("");
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    api
      .get<{ categories: string[] }>("/spend-categories")
      .then((r) => {
        setCategories(r.categories);
        setCategory((c) => c || r.categories[0] || "other");
      })
      .catch(() => {
        setCategories(Object.keys(CATEGORY_LABELS));
        setCategory((c) => c || "snacks");
      });
  }, []);

  if (withCash.length === 0) {
    return (
      <Panel title="I spent something" onCancel={onCancel}>
        <p style={{ margin: 0, color: "var(--ink-2)" }}>
          You don&rsquo;t have any cash in your pocket right now. Ask for some
          first.
        </p>
      </Panel>
    );
  }

  return (
    <Panel title="I spent something" onCancel={onCancel}>
      <p style={{ margin: "0 0 14px", color: "var(--ink-2)", fontSize: 14 }}>
        Write down what you bought with your real money.
      </p>

      <label style={{ display: "grid", gap: 6, marginBottom: 14 }}>
        <span style={{ fontWeight: 600, fontSize: 14 }}>How much did it cost?</span>
        <input
          inputMode="decimal" value={amount}
          onChange={(e) => setAmount(e.target.value)}
          className="money" placeholder="1.50"
          style={{ fontSize: 26, textAlign: "center", minHeight: 60 }}
        />
      </label>

      <span style={{ fontWeight: 600, fontSize: 14, display: "block", marginBottom: 8 }}>
        What was it?
      </span>
      <div
        style={{
          display: "grid",
          gridTemplateColumns: "repeat(auto-fit, minmax(120px, 1fr))",
          gap: 8, marginBottom: 16,
        }}
      >
        {categories.map((key) => (
          <button
            key={key}
            type="button"
            aria-pressed={category === key}
            onClick={() => setCategory(key)}
            style={{
              minHeight: 52, fontSize: 14, boxShadow: "none",
              background: category === key ? "var(--spend-soft)" : "var(--paper)",
              borderColor: category === key ? "var(--spend)" : "var(--paper-edge)",
            }}
          >
            {labelFor(key)}
          </button>
        ))}
      </div>

      {withCash.length > 1 && (
        <label style={{ display: "grid", gap: 6, marginBottom: 14 }}>
          <span style={{ fontWeight: 600, fontSize: 14 }}>Which money?</span>
          <select value={bucketId} onChange={(e) => setBucketId(e.target.value)}>
            {withCash.map((b) => (
              <option key={b.id} value={b.id}>
                {b.name} — {formatCents(b.walletCents)}
              </option>
            ))}
          </select>
        </label>
      )}

      {error && (
        <p role="alert" style={{ color: "var(--bad)", fontWeight: 600, marginTop: 0 }}>
          {error}
        </p>
      )}

      <button
        disabled={busy || !amount || !category}
        style={{ width: "100%", minHeight: 56 }}
        onClick={async () => {
          const cents = Math.round(parseFloat(amount) * 100);
          if (!Number.isFinite(cents) || cents <= 0) {
            setError("Type how much it cost.");
            return;
          }
          setBusy(true);
          setError(null);
          try {
            await api.post(`/children/${childId}/spend`, {
              bucketId, amountCents: cents, category,
            });
            onDone();
          } catch (e) {
            setError(e instanceof ApiError ? e.message : "That didn't work.");
          } finally {
            setBusy(false);
          }
        }}
      >
        {busy ? "Saving…" : "Write it down"}
      </button>
    </Panel>
  );
}

function Panel({
  title,
  children,
  onCancel,
}: {
  title: string;
  children: React.ReactNode;
  onCancel: () => void;
}) {
  return (
    <section className="card" style={{ marginBottom: 16 }}>
      <div
        style={{
          display: "flex", alignItems: "center",
          justifyContent: "space-between", marginBottom: 10,
        }}
      >
        <h2 style={{ fontSize: 20 }}>{title}</h2>
        <button onClick={onCancel} style={{ minHeight: 44, fontSize: 14, padding: "0 14px" }}>
          Close
        </button>
      </div>
      {children}
    </section>
  );
}
